/**
 * Реєстр типів довідників для ReferencePicker.
 * Кожен запис описує endpoint + як побудувати label з item-у.
 *
 * Якщо потрібен новий тип — додати запис тут, без правки самого ReferencePicker.
 */
import httpClient from '@api/httpClient';
import type { PaginatedResult } from '@/types';

export interface ReferenceItem {
  id: string;
  label: string;
  /** Сирий обʼєкт з API (для розширених сценаріїв — типу bound-фільтрів). */
  raw: Record<string, unknown>;
}

export interface ReferenceTypeConfig {
  /** Назва для UI ('Контрагенти'). */
  title: string;
  /** REST endpoint без префіксу /api (наприклад '/counterparties'). */
  endpoint: string;
  /**
   * Як зробити label з API-обʼєкта.
   * Дефолт: name → fullName → code → id.
   */
  toLabel?: (acqRaw: Record<string, unknown>) => string;
  /**
   * Назва параметра пошуку в URL. Дефолт: 'search'.
   * Деякі endpoint можуть приймати 'q' або 'name'.
   */
  searchParam?: string;
  /**
   * Sprint 20+ Refactor "Domain → Meta-каталоги": якщо true, ReferencePicker йде
   * через unified endpoint /api/references/{typeCode} замість типізованого endpoint.
   * Сервер повертає ReferenceItemDto {id, code, label, providerKind} незалежно від
   * того, чи довідник зараз Domain або Meta — зміна Router транспарентна для UI.
   * Включається для довідників, що пройшли Етап 4d.
   */
  useUnified?: boolean;
  /** Код типу для unified endpoint (наприклад 'position'). Лише якщо useUnified=true. */
  unifiedTypeCode?: string;
}

const acqDefaultToLabel = (acqRaw: Record<string, unknown>): string => {
  const acqAny = acqRaw as Record<string, string | undefined>;
  return (
    acqAny.name ?? acqAny.fullName ?? acqAny.code ?? (acqAny.id ? String(acqAny.id) : '')
  );
};

export const REFERENCE_TYPES: Record<string, ReferenceTypeConfig> = {
  counterparty: {
    title: 'Контрагент',
    endpoint: '/counterparties',
    // Етап 6: мігровано на meta. Domain entity лишається живим (FK у Document/JournalEntry).
    useUnified: true,
    unifiedTypeCode: 'counterparty',
  },
  organization: {
    title: 'Організація',
    endpoint: '/organizations',
    // Етап 6: мігровано на meta. ⚠ JournalEntry.OrganizationId — Guid FK на Domain.
    useUnified: true,
    unifiedTypeCode: 'organization',
  },
  currency: {
    title: 'Валюта',
    endpoint: '/currencies',
    toLabel: (r) => {
      const a = r as { code?: string; name?: string };
      return a.code && a.name ? `${a.code} — ${a.name}` : (a.code ?? a.name ?? '');
    },
    // Етап 6: мігровано на meta. ⚠ JournalEntry.CurrencyId — Guid FK на Domain.
    // toLabel не використовується для unified-шляху — сервер сам формує label з JSONB.
    useUnified: true,
    unifiedTypeCode: 'currency',
  },
  bank: {
    title: 'Банк',
    endpoint: '/banks',
    // Етап 5: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'bank',
  },
  bankAccount: {
    title: 'Банківський рахунок',
    endpoint: '/bank-accounts',
    // Етап 5: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'bank_account',
  },
  nomenclature: {
    title: 'Номенклатура',
    endpoint: '/nomenclatures',
    // Етап 6: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'nomenclature',
  },
  nomenclatureGroup: {
    title: 'Група номенклатури',
    endpoint: '/nomenclature-groups',
    // Етап 5: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'nomenclature_group',
  },
  warehouse: {
    title: 'Склад',
    endpoint: '/accounting-warehouses',
    // Порція 2: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'warehouse',
  },
  costItem: {
    title: 'Стаття витрат',
    endpoint: '/cost-items',
    // Порція 2: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'cost_item',
  },
  contract: {
    title: 'Договір',
    endpoint: '/counterparty-contracts',
    // Етап 5: мігровано на meta (counterparty_contract з OwnerType="counterparty").
    useUnified: true,
    unifiedTypeCode: 'counterparty_contract',
  },
  individual: {
    title: 'Фіз. особа',
    endpoint: '/individuals',
    // Етап 5: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'individual',
  },
  employee: {
    title: 'Співробітник',
    endpoint: '/employees',
    toLabel: (r) => {
      const a = r as { individualName?: string; name?: string; code?: string };
      return a.individualName ?? a.name ?? a.code ?? '';
    },
    // Етап 5: мігровано на meta. toLabel не використовується для unified-шляху —
    // сервер сам формує label з JSONB полів (name → full_name → ...).
    useUnified: true,
    unifiedTypeCode: 'employee',
  },
  position: {
    title: 'Посада',
    endpoint: '/positions',
    // Sprint 20+ Refactor Етап 4e: position мігровано на meta + Router → MetaProvider.
    // Тепер ReferencePicker йде через /api/references/position. Старий endpoint
    // /positions ще працює (Domain controller), але UI його не викликає.
    useUnified: true,
    unifiedTypeCode: 'position',
  },
  fixedAsset: {
    title: 'Основний засіб',
    endpoint: '/fixed-assets',
    // Порція 2: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'fixed_asset',
  },
  intangibleAsset: {
    title: 'Нематеріальний актив',
    endpoint: '/intangible-assets',
    // Порція 2: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'intangible_asset',
  },
  chartOfAccount: {
    title: 'Рахунок плану рахунків',
    endpoint: '/chart-of-accounts',
    toLabel: (r) => {
      const a = r as { code?: string; name?: string };
      return a.code && a.name ? `${a.code} ${a.name}` : (a.code ?? a.name ?? '');
    },
    // Етап 6: мігровано на meta. JournalEntry.DebitAccount/CreditAccount — string code,
    // не Guid FK, тому міграція безпечна. toLabel не використовується для unified-шляху.
    useUnified: true,
    unifiedTypeCode: 'chart_of_account',
  },
  organizationDepartment: {
    title: 'Підрозділ',
    endpoint: '/organization-departments',
    // Етап 5: мігровано на meta (з OwnerType="organization", IsHierarchical=true).
    useUnified: true,
    unifiedTypeCode: 'organization_department',
  },
  vatRate: {
    title: 'Ставка ПДВ',
    endpoint: '/vat-rates',
    toLabel: (r) => {
      const a = r as { name?: string; rate?: number };
      return a.rate != null ? `${a.name ?? ''} (${a.rate}%)` : (a.name ?? '');
    },
  },
  priceType: {
    title: 'Тип ціни',
    endpoint: '/price-types',
    // Етап 5: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'price_type',
  },
  taxActivityType: {
    title: 'Вид податкової діяльності',
    endpoint: '/tax-activity-types',
    // Порція 1 Sprint 20+ Refactor: мігровано на meta (Router → MetaProvider).
    useUnified: true,
    unifiedTypeCode: 'tax_activity_type',
  },
  settlementType: {
    title: 'Вид взаєморозрахунків',
    endpoint: '/settlement-types',
    // Етап 5: мігровано на meta.
    useUnified: true,
    unifiedTypeCode: 'settlement_type',
  },
  // Phase 7g — well-known BAS-довідники без типізованого Domain CRUD-UI, мігровано на meta.
  cashFlowItem: {
    title: 'Стаття руху грошових коштів',
    endpoint: '/bas-refs/CashFlowItem',
    useUnified: true,
    unifiedTypeCode: 'cash_flow_item',
  },
  tax: {
    title: 'Податок',
    endpoint: '/bas-refs/Tax',
    useUnified: true,
    unifiedTypeCode: 'tax',
  },
  taxPurpose: {
    title: 'Податкове призначення',
    endpoint: '/bas-refs/TaxPurpose',
    useUnified: true,
    unifiedTypeCode: 'tax_purpose',
  },
  taxDeclarationItem: {
    title: 'Стаття податкової декларації',
    endpoint: '/bas-refs/TaxDeclarationItem',
    useUnified: true,
    unifiedTypeCode: 'tax_declaration_item',
  },
  accrualGroup: {
    title: 'Група нарахувань/утримань',
    endpoint: '/bas-refs/AccrualGroup',
    useUnified: true,
    unifiedTypeCode: 'accrual_group',
  },
  user: {
    title: 'Користувач',
    endpoint: '/users',
    toLabel: (r) => {
      const a = r as { userName?: string; fullName?: string; email?: string };
      return a.fullName ?? a.userName ?? a.email ?? '';
    },
  },
};

/** Перевірка, що такий referenceType існує в реєстрі. */
export function acqIsKnownReferenceType(acqType: string): boolean {
  return acqType in REFERENCE_TYPES;
}

/**
 * Мостик BAS-import → ReferencePicker. BasMetadataParser зберігає
 * MetaAttribute.referenceEntity у форматі C#-сутності ("Counterparty",
 * "Currency", ...) — це ключ нашого Domain. Фронтовий реєстр використовує
 * camelCase. Ця функція мапить одне в інше.
 *
 * Повертає null якщо тип невідомий (BasRef:..., BasEnum:..., Subconto і т.д.) —
 * у такому разі ReferencePicker не використовується, поле лишається read-only.
 */
const acqEntityToRefType: Record<string, string> = {
  Counterparty: 'counterparty',
  Organization: 'organization',
  Currency: 'currency',
  Bank: 'bank',
  BankAccount: 'bankAccount',
  Nomenclature: 'nomenclature',
  NomenclatureGroup: 'nomenclatureGroup',
  AccountingWarehouse: 'warehouse',
  CostItem: 'costItem',
  CounterpartyContract: 'contract',
  Individual: 'individual',
  Employee: 'employee',
  Position: 'position',
  FixedAsset: 'fixedAsset',
  IntangibleAsset: 'intangibleAsset',
  ChartOfAccount: 'chartOfAccount',
  OrganizationDepartment: 'organizationDepartment',
  PriceType: 'priceType',
  VatRate: 'vatRate',
  TaxActivityType: 'taxActivityType',
  SettlementType: 'settlementType',
  User: 'user',
  // Phase 7g — well-known BAS-довідники мігровано на meta + ReferencePicker (раніше — BasRefPicker default).
  CashFlowItem: 'cashFlowItem',
  Tax: 'tax',
  TaxPurpose: 'taxPurpose',
  TaxDeclarationItem: 'taxDeclarationItem',
  AccrualGroup: 'accrualGroup',
  // Невідомі (немає типізованого CRUD-UI):
  //   Document (polymorphic doc-ref) — DocumentPicker.
  //   BasRef:* (без AcQuantum-entity) — сирі BAS-довідники, BasExternalRefPicker → /api/bas-refs/external/{basTypeName}.
  //   Subconto — полиморфне через xsi:type, окремий пікер.
  //   Enum (BasEnum:*) — EnumPicker + meta_enums таблиця.
};

/**
 * Catch-all для довідників, що приходять з BAS Phase 0 з префіксом `BasRef:`.
 * Ці типи НЕ імпортовані в Domain і використовують BasExternalRef-таблицю
 * (заповнюється Phase 1 importer'ом). На фронті ми просто беремо BAS-ім'я
 * (типу `Сделки`) і шлемо у /api/bas-refs/external/{basTypeName}.
 */
export function acqParseBasRefExternal(acqEntityName: string | null | undefined): { basTypeName: string } | null {
  if (!acqEntityName) return null;
  if (!acqEntityName.startsWith('BasRef:')) return null;
  return { basTypeName: acqEntityName.substring('BasRef:'.length) };
}

export function acqEntityNameToReferenceType(acqEntityName: string | null | undefined): string | null {
  if (!acqEntityName) return null;
  return acqEntityToRefType[acqEntityName] ?? null;
}

/** Для UI — перелік усіх типів (наприклад у Property panel конструктора форм). */
export function acqListReferenceTypes(): { value: string; label: string }[] {
  return Object.entries(REFERENCE_TYPES).map(([acqKey, acqCfg]) => ({
    value: acqKey,
    label: acqCfg.title,
  }));
}

/** Серверний пошук за рядком (для async-Select). */
export async function acqSearchReference(
  acqType: string,
  acqQuery: string,
  acqLimit = 20,
): Promise<ReferenceItem[]> {
  const acqCfg = REFERENCE_TYPES[acqType];
  if (!acqCfg) throw new Error(`Unknown reference type: ${acqType}`);

  // Unified-shлях: /api/references/{typeCode}?qSearch=...&qPageSize=...
  // Сервер повертає вже сформований label незалежно від Domain/Meta провайдера.
  if (acqCfg.useUnified && acqCfg.unifiedTypeCode) {
    const acqParams: Record<string, unknown> = { qPage: 1, qPageSize: acqLimit };
    if (acqQuery) acqParams.qSearch = acqQuery;
    const acqRes = await httpClient.get<{ items: { id: string; code: string; label: string }[] }>(
      `/references/${acqCfg.unifiedTypeCode}`,
      { params: acqParams },
    );
    return (acqRes.data.items ?? []).map((acqIt) => ({
      id: acqIt.id,
      label: acqIt.label || `[без назви #${acqIt.id.slice(0, 6)}]`,
      raw: acqIt as unknown as Record<string, unknown>,
    }));
  }

  const acqParams: Record<string, unknown> = { page: 1, pageSize: acqLimit };
  if (acqQuery) acqParams[acqCfg.searchParam ?? 'search'] = acqQuery;

  // Більшість endpoint повертає PaginatedResult; деякі простіші — масив (currencies).
  const acqRes = await httpClient.get<PaginatedResult<Record<string, unknown>> | Record<string, unknown>[]>(
    acqCfg.endpoint,
    { params: acqParams },
  );

  const acqList: Record<string, unknown>[] = Array.isArray(acqRes.data)
    ? acqRes.data
    : (acqRes.data.items ?? []);

  const acqLabel = acqCfg.toLabel ?? acqDefaultToLabel;
  return acqList.map((acqIt) => ({
    id: String(acqIt.id),
    label: acqLabel(acqIt) || `[без назви #${String(acqIt.id).slice(0, 6)}]`,
    raw: acqIt,
  }));
}

/** Резолв одного запису за id (для відображення вже збереженого value).
 *  Прямий GET /{endpoint}/{id}. Усі контролери довідників мають GetById endpoint
 *  (Sprint 17 — додано 21 endpoint з MediatR-Query + Handler).
 */
export async function acqGetReferenceById(
  acqType: string,
  acqId: string,
): Promise<ReferenceItem | null> {
  const acqCfg = REFERENCE_TYPES[acqType];
  if (!acqCfg) return null;

  // Unified-шлях: /api/references/{typeCode}/{id}
  if (acqCfg.useUnified && acqCfg.unifiedTypeCode) {
    try {
      const acqRes = await httpClient.get<{ id: string; code: string; label: string }>(
        `/references/${acqCfg.unifiedTypeCode}/${acqId}`,
      );
      if (acqRes.data && acqRes.data.id) {
        return {
          id: acqRes.data.id,
          label: acqRes.data.label || `[без назви #${acqRes.data.id.slice(0, 6)}]`,
          raw: acqRes.data as unknown as Record<string, unknown>,
        };
      }
    } catch { return null; }
    return null;
  }

  const acqLabel = acqCfg.toLabel ?? acqDefaultToLabel;
  try {
    const acqRes = await httpClient.get<Record<string, unknown>>(`${acqCfg.endpoint}/${acqId}`);
    if (acqRes.data && acqRes.data.id) {
      return {
        id: String(acqRes.data.id),
        label: acqLabel(acqRes.data) || `[без назви #${String(acqRes.data.id).slice(0, 6)}]`,
        raw: acqRes.data,
      };
    }
  } catch {
    return null;
  }
  return null;
}
